import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const DEFAULTS = {
    logoUrl: 'https://res.cloudinary.com/da8ljr91k/image/upload/v1753806510/dodashX_bxstdi.png',
    footerIconUrl: 'https://res.cloudinary.com/da8ljr91k/image/upload/v1753806510/dodashX_bxstdi.png',
    addQRCode: true,
    qrContent: 'https://monapp.com/rapport/stock/global',
    company: {
        name: 'DodashX Corporation',
        email: 'contact@dodashx.com',
        phone: '+229 99 99 99 99',
        website: 'https://dodashx.com'
    }
};

async function getImageAsBase64(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
}

export async function exportPDF({ title, table, filename }) {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('fr-FR');
    const pageWidth = doc.internal.pageSize.getWidth();

    const [logoBase64, iconBase64, qrBase64] = await Promise.all([
        getImageAsBase64(DEFAULTS.logoUrl),
        getImageAsBase64(DEFAULTS.footerIconUrl),
        DEFAULTS.addQRCode ? QRCode.toDataURL(DEFAULTS.qrContent) : null
    ]);

    autoTable(doc, {
        startY: 50,
        head: table.head,
        body: table.body,
        foot: table.foot,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [52, 152, 219], textColor: 255 },
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
        didDrawPage: (data) => {
            const pageHeight = doc.internal.pageSize.height;

            if (logoBase64) doc.addImage(logoBase64, 'PNG', 14, 8, 35, 15);
            if (iconBase64) doc.addImage(iconBase64, 'PNG', pageWidth - 40, pageHeight - 15, 20, 10);
            if (qrBase64) doc.addImage(qrBase64, 'PNG', pageWidth - 60, 8, 30, 30);

            doc.setFontSize(10);
            doc.setTextColor(40);
            doc.text(DEFAULTS.company.name, 60, 12);
            doc.setFontSize(8);
            doc.text(`✉ ${DEFAULTS.company.email}`, 60, 17);
            doc.text(`📞 ${DEFAULTS.company.phone}`, 60, 21);
            doc.text(`🌐 ${DEFAULTS.company.website}`, 60, 25);

            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text(`📆 Exporté le : ${today}`, pageWidth - 65, 15);
            doc.setFontSize(11);
            doc.text(title, pageWidth / 2, 35, { align: 'center' });

            doc.setDrawColor(150);
            doc.line(14, 30, pageWidth - 14, 30);

            doc.setFontSize(8);
            doc.setTextColor(120);
            doc.text(`Page ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, pageHeight - 10);
        }
    });

    doc.save(filename || 'rapport.pdf');
}


export function exportToExcel({ title = 'Rapport', headers = [], data = [], filename = 'rapport.xlsx' }) {
    // 1. Créer les lignes avec entête
    const worksheetData = [headers, ...data];

    // 2. Créer une feuille et un workbook
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title);

    // 3. Générer le fichier Excel
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, filename);
}

export async function printInvoicePDF(sale, saleLines) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleDateString('fr-FR');

    const [logoBase64, qrBase64] = await Promise.all([
        getImageAsBase64(DEFAULTS.logoUrl),
        DEFAULTS.addQRCode ? QRCode.toDataURL(DEFAULTS.qrContent + `/${sale.reference}`) : null
    ]);

    if (logoBase64) doc.addImage(logoBase64, 'PNG', 14, 10, 40, 15);
    doc.setFontSize(10);
    doc.text(`Facture N° ${sale.reference}`, pageWidth - 60, 15);
    doc.setFontSize(8);
    doc.text(`Date : ${new Date(sale.saleDate).toLocaleDateString()}`, pageWidth - 60, 20);

    doc.setFontSize(10);
    doc.text(`Client : ${sale.clientName || 'N/A'}`, 14, 35);
    doc.text(`Vendeur : ${sale.sellerName || 'N/A'}`, 14, 40);

    autoTable(doc, {
        startY: 50,
        head: [['Produit', 'Qté', 'PU (FCFA)', 'Total (FCFA)']],
        body: saleLines.map(line => [
            line.productName,
            line.quantity,
            line.unitPrice.toFixed(2),
            (line.quantity * line.unitPrice).toFixed(2)
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [52, 152, 219], textColor: 255 },
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFontSize(10);
    doc.text(`Montant Total : ${sale.totalAmount.toFixed(2)} FCFA`, 14, finalY);
    if (sale.discountAmount) {
        doc.text(`Remise : -${sale.discountAmount.toFixed(2)} FCFA`, 14, finalY + 5);
    }
    if (sale.taxAmount) {
        doc.text(`TVA : +${sale.taxAmount.toFixed(2)} FCFA`, 14, finalY + 10);
    }

    doc.text(`Montant à payer : ${(sale.totalAmount + (sale.taxAmount || 0) - (sale.discountAmount || 0)).toFixed(2)} FCFA`, 14, finalY + 15);

    if (qrBase64) {
        doc.addImage(qrBase64, 'PNG', pageWidth - 40, finalY, 25, 25);
    }

    doc.save(`Facture_${sale.reference}.pdf`);
}
